<template>
  <div class="head" ref="thisHead">
    <!-- AH: Can these head-outline classes be removed? -->
    <div
      class="head-outline"
      :class="{
        'head-outline-solo': kind === 'solo' || kind === 'teamsolo' || kind === 'orgsolo',
        'head-outline-viewpoints': kind === 'viewpoints',
        'head-outline-team-effectiveness':
          kind === 'TeamEffectivenessAggregateDiamond' ||
          kind === 'TeamEffectivenessIndividualDiamond',
        'head-outline-solo-pair': (kind === 'solo' || kind === 'teamsolo') && whiteBackground,
        'head-outline-viewpoints-pair':
          (kind === 'viewpoints' || kind === 'teamviewpoints' || kind === 'orgviewpoints') &&
          whiteBackground,
      }"
    >
      <div class="head-background" data-test="head-background-img" />
      <div class="head-dot-container">
        <div class="head-crosshair-horizontal" />
        <div class="head-crosshair-vertical" />

        <div v-if="!!values && !!containerForTooltips">
          <Tooltip
            tag="div"
            :customClass="tooltipClass"
            v-for="(leaderType, index) in leaderTypes"
            :key="index"
            :text="tooltipText(leaderType)"
            :transition="0"
            :enterable="false"
            :append-to="containerForTooltips"
          >
            <div
              :style="style(leaderType)"
              class="head-dot"
              :class="getStyles(leaderType, kind)"
              @click="emitLeaderTypeSelect(leaderType)"
              @keydown.space.prevent="emitLeaderTypeSelect(leaderType)"
              :data-test="`head-dot-${leaderType}`"
              :leader-type="leaderType"
            >
              <!-- <div class="diamond-head-hit-target"></div> -->
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { TimelineMax, Back } from "gsap";
import { Tooltip } from "uiv";
import cms from "../../services/cms/cms";
import cmsContentMap from "../../services/cms/cms-content-map";
import { CmsTeamEffectivenessReview } from "../../services/cms/cms-type-definitions";
import { HeadValueTypeApiModel, LeaderTypesApiModel } from "../../api-models";
import { Kind, FillOptions } from "./head-models";

const props = withDefaults(
  defineProps<{
    animateEntrance?: boolean;
    kind: Kind;
    values?: Record<string, number> | null;
    valueType: HeadValueTypeApiModel;
    showTooltips?: boolean;
    fillOverride?: FillOptions | null;
    containerForTooltips?: string;
    whiteBackground?: boolean;
  }>(),
  {
    values: null,
    animateEntrance: false,
    fillOverride: null,
    showTooltips: true,
    containerForTooltips: "body",
    whiteBackground: true,
  },
);

const emit = defineEmits(["leaderTypeSelection"]);

// template refs
const thisHead = ref<HTMLElement | null>(null);

function getDotsByAscendingSize(dots: HTMLElement[]): HTMLElement[] {
  return dots.sort((a, b) => {
    const aWidth = parseFloat(a.style.width);
    const bWidth = parseFloat(b.style.width);
    if (!aWidth) {
      return -1;
    }
    if (!bWidth) {
      return 1;
    }
    if (aWidth < bWidth) {
      return -1;
    }
    if (aWidth > bWidth) {
      return 1;
    }

    return 0;
  });
}

const teamEffectivenessReview = ref<CmsTeamEffectivenessReview | null>(null);

const tooltipClass = computed(() => {
  return props.containerForTooltips !== "body" ? "head-tooltips-fixed" : "head-tooltips";
});

const isTeamEffectivenessHead = computed(() => {
  return (
    props.kind === "TeamEffectivenessAggregateDiamond" ||
    props.kind === "TeamEffectivenessIndividualDiamond"
  );
});

function getTeamEffectivenessDoc() {
  if (isTeamEffectivenessHead.value) {
    cms
      .getByUid("cms-team-effectiveness-review", cmsContentMap.teamEffectiveness.questionnaireUid)
      .then((doc) => {
        teamEffectivenessReview.value = doc;
      });
  }
}

getTeamEffectivenessDoc();

const leaderTypes: LeaderTypesApiModel[] = [
  "Professional",
  "Entrepreneurial",
  "Executional",
  "Practical",
  "Strategist",
  "Servant",
  "Transformational",
  "Charismatic",
  "Innovator",
];

function emitLeaderTypeSelect(leaderType: string): void {
  emit("leaderTypeSelection", leaderType);
}

function getStyles(leaderType: string, kind: Kind): string[] {
  return [
    `dot-${leaderType.toLowerCase()} head-kind-${kind} ${
      props.fillOverride ? `fill-override-${props.fillOverride}` : ""
    }`,
  ];
}

const axisScale = 18;
const spotScale = 0.3;

function getSizePercent(value: number) {
  return (props.valueType === "DecimalValue" ? Math.floor(value * 10) : value) * spotScale;
}

function style(leaderType: string): Record<string, string | number> {
  if (!props.values) {
    return {
      display: "none",
    };
  }

  const positions: Record<LeaderTypesApiModel, { x: number; y: number }> = {
    Innovator: { x: 0, y: -2 },
    Entrepreneurial: { x: -1, y: -1 },
    Executional: { x: -2, y: -0 },
    Practical: { x: -1, y: 1 },
    Strategist: { x: 0, y: 2 },
    Servant: { x: 1, y: 1 },
    Transformational: { x: 2, y: 0 },
    Charismatic: { x: 1, y: -1 },
    Professional: { x: 0, y: 0 },
  };

  const x = positions[leaderType].x * axisScale;
  const y = positions[leaderType].y * axisScale;

  const sizePercent = getSizePercent(props.values[leaderType]);
  // const widthAndHeight = props.willAnimate ? 0 : `${sizePercent}%`;
  const widthAndHeight = `${sizePercent}%`;

  return {
    left: `${x - sizePercent * 0.5 + 50}%`,
    top: `${y - sizePercent * 0.5 + 50}%`,
    width: widthAndHeight,
    height: widthAndHeight,
    zIndex: 2,
    position: "absolute",
    opacity: 0.9,
  };
}

function getValuesByAscendingSize() {
  if (!props.values) return null;
  // Step 1: Convert the object into an array of [key, value] pairs
  const entries = Object.entries(props.values);

  // Step 2: Sort the array based on the values
  const sortedEntries = entries.sort((a, b) => a[1] - b[1]);

  return sortedEntries;
}

function getDot(leaderType: string) {
  return thisHead.value?.querySelector(`.head-dot[leader-type="${leaderType}"]`) as HTMLElement;
}

function runEntranceAnimation(): void {
  const values = getValuesByAscendingSize();
  if (!values) return;
  const timeline = new TimelineMax();
  for (let i = 0; i < values.length; i++) {
    timeline.from(
      getDot(values[i][0]),
      0.3,
      { height: 0, width: 0, delay: i * 0.2, ease: Back.easeOut },
      0,
    );
    getDot(values[i][0]).style.zIndex = String(2 + i);
  }
}

function onAnimateChange() {
  if (props.animateEntrance) {
    setTimeout(() => {
      runEntranceAnimation();
    });
  }
}

watch(() => props.animateEntrance, onAnimateChange);

function getAllStylesByLeaderType(): Record<string, Record<string, string | number>> {
  return leaderTypes
    .map((leaderType) => {
      return { leaderType, styles: style(leaderType) };
    })
    .reduce((obj, value) => {
      obj[value.leaderType] = value.styles;
      return obj;
    }, {});
}

function tooltipText(leaderType: string): string {
  if (!props.showTooltips) {
    return ""; // Empty tooltips are not displayed at all;
  }

  if (!props.values) {
    return "";
  }

  if (isTeamEffectivenessHead.value) {
    const text = cms.asText(
      teamEffectivenessReview.value?.data.body.find(
        (slice) => slice.primary.question_id === leaderType,
      )?.primary.question_title,
    );

    return `${text}: ${props.values[leaderType].toFixed(1)}`;
  }

  return `${leaderType}: ${props.values[leaderType]}% fit`;
}

// this is so cms-session can trigger runEntranceAnimation during a session
function playEntranceAnimationForSessions(): void {
  runEntranceAnimation();
}

onMounted(onAnimateChange);

defineExpose({
  playEntranceAnimationForSessions,
  getAllStylesByLeaderType,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.head {
  position: relative;
  z-index: 1;
}

.head-outline {
  width: 100%;
  display: inline-block;
  position: relative;

  .head-kind-solo,
  .head-kind-teamsolo,
  .head-kind-orgsolo {
    border-radius: 50%;
  }

  .head-kind-viewpoints,
  .head-kind-teamviewpoints,
  .head-kind-orgviewpoints {
    transform: rotate(45deg);
  }

  .head-background {
    width: 100%;
    height: 100%;
    position: absolute;
    background-size: cover;
    background-image: url("../../assets/head-outlines/head-viewpoints-outline.svg");
  }

  .head-dot-container {
    position: absolute;
    left: 20%;
    top: 8%;
    width: 70%;
    padding-bottom: 70%;
  }

  &:after {
    padding-top: 115%;
    display: block;
    content: "";
  }

  .head-dot {
    cursor: pointer;

    &:hover {
      filter: brightness(0.75);
    }
  }

  .dotLeaderType(@name) {
    .dot-@{name} {
      @colorName: "@{name}-color";
      background-color: @@colorName;
      border-color: @@colorName;
    }
  }

  .dotLeaderType(innovator);
  .dotLeaderType(entrepreneurial);
  .dotLeaderType(executional);
  .dotLeaderType(practical);
  .dotLeaderType(strategist);
  .dotLeaderType(servant);
  .dotLeaderType(transformational);
  .dotLeaderType(charismatic);
  .dotLeaderType(professional);

  .head-kind-teamsolo:not(.fill-override-solid),
  .head-kind-teamviewpoints:not(.fill-override-solid),
  .head-kind-TeamEffectivenessAggregateDiamond:not(.fill-override-solid),
  .fill-override-hollow {
    background-color: transparent;
    border-width: 4px;
    border-style: solid;
  }
}

.head-outline-solo,
.head-outline-team-effectiveness {
  .head-background {
    background-image: url("../../assets/head-outlines/head-solo-outline.svg");
  }

  .head-dot-container {
    left: 10%;
  }
}

.head-outline-viewpoints {
  .head-background {
    background-image: url("../../assets/head-outlines/head-viewpoints-outline.svg");
  }
}

.head-outline-solo-pair {
  .head-background {
    background-image: url("../../assets/head-outlines/head-solo-outline-fill.svg");
  }
}

.head-outline-viewpoints-pair {
  .head-background {
    background-image: url("../../assets/head-outlines/head-viewpoints-outline-fill.svg");
  }
}

.head-outline-grayscale-outlines {
  .head-background {
    background-image: url("../../assets/head-outlines/head-viewpoints-grayscale-outlines.png");
  }
}

.head-outline-grayscale-outlines.head-outline-solo {
  .head-background {
    background-image: url("../../assets/head-outlines/head-solo-grayscale-outlines.png");
  }
}

.head-crosshair-horizontal {
  background-color: fade(black, 12%);
  position: absolute;
  top: 50%;
  left: 0;
  height: 2px;
  width: 100%;
  -webkit-transform: translate(0, -1px);
  -moz-transform: translate(0, -1px);
  -o-transform: translate(0, -1px);
  -ms-transform: translate(0, -1px);
  transform: translate(0, -1px);
}

.extendedCrosshair .head-crosshair-horizontal {
  left: -100%;
  width: 300%;
}

.head-crosshair-vertical {
  background-color: fade(black, 12%);
  position: absolute;
  left: 50%;
  top: 0;
  width: 2px;
  height: 100%;
  -webkit-transform: translate(-1px, 0);
  -moz-transform: translate(-1px, 0);
  -o-transform: translate(-1px, 0);
  -ms-transform: translate(-1px, 0);
  transform: translate(-1px, 0);
}

.head-dot-container {
  width: 100%;
  padding-bottom: 100%;
  position: relative;
}

// .head-dot-container.transparentDiamonds .diamondButton {
//   background-color: transparent;
// }

// .head-hover-target {
//   border-radius: 12px;
//   height: 24px;
//   width: 24px;
//   position: relative;
//   top: calc(50% - 12px);
//   left: calc(50% - 12px);
// }
</style>

<style lang="less">
/* This stops the tooltips interfering with scale animations */
.head-tooltips {
  pointer-events: none;
}

.head-tooltips-fixed {
  .head-tooltips();
  position: fixed !important;
}
</style>
